import type { EChartsOption } from '../echarts-types.ts';
import type { ChartConfig } from '../types.ts';
import { bodyTooltip, chartTitle, type OptionContext } from './shared.ts';

export function buildHeatmapOption(
  config: Extract<ChartConfig, { type: 'heatmap' }>,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor } = theme;
  const { cols, rows, values } = config;
  if (cols.length === 0 || rows.length === 0) {
    return base;
  }
  const expected = cols.length * rows.length;
  const vals =
    values.length >= expected ? values.slice(0, expected) : [...values];
  while (vals.length < expected) vals.push(0);
  const data: [number, number, number][] = [];
  let k = 0;
  for (let yi = 0; yi < rows.length; yi++) {
    for (let xi = 0; xi < cols.length; xi++) {
      data.push([xi, yi, vals[k] ?? 0]);
      k++;
    }
  }
  const vmin = Math.min(...vals);
  const vmax = Math.max(...vals, vmin + 1e-6);
  return {
    ...base,
    title: chartTitle(config.title, textColor, 'center'),
    tooltip: bodyTooltip({ position: 'top' }),
    grid: {
      left: 8,
      right: 16,
      top: config.title ? 44 : 14,
      bottom: 52,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: cols,
      splitArea: { show: true },
      axisLabel: { color: textColor, fontSize: 10 },
    },
    yAxis: {
      type: 'category',
      data: rows,
      splitArea: { show: true },
      axisLabel: { color: textColor, fontSize: 10 },
    },
    visualMap: {
      min: vmin,
      max: vmax,
      calculable: false,
      orient: 'horizontal',
      left: 'center',
      bottom: 6,
      // Local coords: width = thickness, height = length (then rotated for horizontal).
      itemWidth: 12,
      itemHeight: 160,
      seriesIndex: 0,
      inRange: { color: [colors.stroke, colors.primary] },
      textStyle: { color: textColor, fontSize: 9 },
    },
    series: [
      {
        type: 'heatmap',
        coordinateSystem: 'cartesian2d',
        data,
        label: { show: true, fontSize: 9, color: textColor },
        emphasis: {
          itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.25)' },
        },
      },
    ],
  };
}
